import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import { FolderMap } from '@tempo-io/sui-icons';
import { Popover, PopoverTrigger } from '@tempo-io/sui-popover';
import { ScrollArea } from '@tempo-io/sui-scrollarea';
import { Popover as RadixPopover } from 'radix-ui';
import { useTranslation } from 'react-i18next';
import { Colors } from '@/lib/utils/common/colors';
import { usePortalContainer } from '../../PortalContainerContext';
import { RADIX_POPOVER_CONTENT_BASE } from '../../radixBypassStyles';
import type { VisualizationColorItem } from './types';
import { VisualizationColorSwatch } from './VisualizationColorSwatch';

interface VisualizationColorLegendPopoverProps {
  items: VisualizationColorItem[];
  title: string;
}

export function VisualizationColorLegendPopover({
  items,
  title,
}: VisualizationColorLegendPopoverProps) {
  const { t } = useTranslation();
  const portalContainer = usePortalContainer();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          aria-label={t(($) => $.loopVisualization.layout.colorLegend.open)}
          className={`h-10 w-10 p-0 rounded-full ${Colors.black.bg} ${Colors.white.text}`}
        >
          <FolderMap size={16} />
        </Button>
      </PopoverTrigger>

      {/* RadixPopover.Content is used directly (instead of the SUI PopoverContent wrapper)
          so we can supply a custom portal container in fullscreen mode. The SUI wrapper does
          not expose a `container` prop on its Portal. When the view is fullscreen the
          Fullscreen API only shows the fullscreen element's descendants, so portalling to
          document.body would make this popover invisible. Track SUI portal support: TODO add
          ticket link. The class string is sourced from RADIX_POPOVER_CONTENT_BASE. */}
      <RadixPopover.Portal container={portalContainer ?? undefined}>
        <RadixPopover.Content
          data-slot="popover-content"
          side="top"
          align="end"
          sideOffset={4}
          className={cn(
            RADIX_POPOVER_CONTENT_BASE,
            'w-[228px] p-0 overflow-hidden rounded-[12px] bg-transparent border-0',
          )}
        >
          <div className="w-full h-full rounded-[12px] bg-bg-surface-base/70 backdrop-blur-xs border border-border-neutral-subtle">
            <div className={`px-3 py-2 text-sm font-semibold ${Colors.black.text}`}>{title}</div>

            <ScrollArea className="h-64 overflow-hidden w-full" type="always">
              <div className="w-full pb-2">
                {items.map((item) => (
                  <VisualizationColorSwatch
                    key={item.id}
                    color={item.color}
                    label={item.label}
                    isHidden={item.isHidden}
                  />
                ))}
              </div>
            </ScrollArea>
          </div>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </Popover>
  );
}
